import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import DataTable from "react-data-table-component";
import AddZoneOffCanvasWizard from "../../components/Wizards/AddZoneOffCanvasWizard";
import StarekCinfirmationModal from "../../components/StarekConfirmationModal";
import "../../starek.css";
import { saveZone } from "../../services/RepositoryService";

function Zones() {
    const starekContext = useContext(StarekContext);
    const navigator = useNavigate();

    const GET_ZONE_LIST_FOR_PLANT = gql`
        query GetZones($plantId: Int!) {
            zones(where: { plant_id: { equals: $plantId } }) {
                id
                parent_id
                name
                description
                created_at
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const GET_SHADOW_DEVICES = gql`
        query GetShadowDevices {
            shadowDevices {
                id
                name
            }
        }
    `;

    const DELETE_MANY_ZONES = gql`
        mutation DeleteManyZone($ids: [Int!]!) {
            deleteManyZone(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const DELETE_ONE_ZONE = gql`
        mutation DeleteOneZone($id: Int!) {
            deleteOneZone(where: { id: $id }) {
                id
            }
        }
    `;

    const CREATE_ONE_ZONE = gql`
        mutation CreateOneZone($data: ZoneCreateInput!) {
            createOneZone(data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const UPDATE_ONE_ZONE = gql`
        mutation UpdateOneZone(
            $where: ZoneWhereUniqueInput!
            $data: ZoneUpdateInput!
        ) {
            updateOneZone(where: $where, data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const { data, loading, error, refetch, client } = useQuery(
        GET_ZONE_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { plantId: starekContext.lastUsedPlant.id },
        }
    );

    const [createOneZone] = useMutation(CREATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });
    const [updateOneZone] = useMutation(UPDATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [expanded, setExpanded] = useState([true]);
    const [showAddZoneDrawer, setShowAddZoneDrawer] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [viewMode, setViewMode] = useState("tree");
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [confirmationModalVisible, setConfirmationModalVisible] =
        useState(false);
    const [onConfirmAction, setOnConfirmAction] = useState(null);
    const [deleteManyZone] = useMutation(DELETE_MANY_ZONES);
    const [deleteOneZone] = useMutation(DELETE_ONE_ZONE);
    const [deletingNodeId, setDeletingNodeId] = useState(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error]);

    useEffect(() => {
        if (!data) return;
        setItems(data.zones);
    }, [data]);

    useEffect(() => {
        if (items.length > 0)
            setExpanded(items.map((item) => item.id.toString()));
    }, [items]);

    const parentOptions = useMemo(() => {
        return items.map((z) => ({ value: z.id, label: z.name }));
    }, [items]);

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: {
            style: {
                minHeight: "60px",
            },
        },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    // const deviceOptions = useMemo(
    //   () => [
    //     { value: 101, label: "Device 1" },
    //     { value: 102, label: "Device 2" },
    //     { value: 103, label: "Device 3" },
    //     { value: 104, label: "Device 4" },
    //   ],
    //   []
    // );

    const {
        data: shadowData,
        loading: shadowLoading,
        error: shadowError,
    } = useQuery(GET_SHADOW_DEVICES, {
        client: starekContext.engineClient,
        fetchPolicy: "cache-first",
    });
    useEffect(() => {
        if (shadowError) starekContext.showError(shadowError);
    }, [shadowError]);

    const deviceOptions = useMemo(() => {
        const list = shadowData?.shadowDevices ?? [];
        return list.map((d) => ({ value: d.id, label: d.name || d.id }));
    }, [shadowData]);

    const openConfirm = (fn) => {
        setOnConfirmAction(() => fn);
        setConfirmationModalVisible(true);
    };
    const closeConfirm = () => {
        setOnConfirmAction(null);
        setConfirmationModalVisible(false);
    };

    const handleToggle = (_event, nodeIds) => {
        setExpanded((prev) =>
            prev.includes(nodeIds[0])
                ? prev.filter((id) => id !== nodeIds[0])
                : [...prev, nodeIds[0]]
        );
    };

    const handleSaveZone = async (zonePayload) => {
        const currentUserId =
            starekContext?.currentUser?.id ??
            starekContext?.user?.id ??
            starekContext?.me?.id;

        if (!currentUserId) {
            console.error(
                "Missing currentUserId for created_by/updated_by connect"
            );
            throw new Error(
                "No current user in context; cannot set created_by/updated_by."
            );
        }
        const base = {
            name: zonePayload.name,
            description: zonePayload.description || "",
            plant: { connect: { id: Number(zonePayload.plant_id) } },
        };

        if (!zonePayload.id) {
            const data = {
                ...base,
                is_active: zonePayload.is_active ?? true,
                ...(zonePayload.parent_id != null
                    ? {
                          parent: {
                              connect: { id: Number(zonePayload.parent_id) },
                          },
                      }
                    : {}),
                ...(Array.isArray(zonePayload.selected_device_ids) &&
                zonePayload.selected_device_ids.length > 0
                    ? (() => {
                          const ids = zonePayload.selected_device_ids
                              .map((id) => Number(id))
                              .filter(Number.isFinite);
                          return ids.length > 0
                              ? {
                                    installed_devices: {
                                        connect: ids.map((id) => ({ id })),
                                    },
                                }
                              : {};
                      })()
                    : {}),
                created_by: { connect: { id: Number(currentUserId) } },
                updated_by: { connect: { id: Number(currentUserId) } },
            };
            await createOneZone({ variables: { data } });
        } else {
            const data = {
                name: { set: base.name },
                description: { set: base.description },
                plant: base.plant,
                ...(zonePayload.parent_id === null
                    ? { parent: { disconnect: true } }
                    : zonePayload.parent_id !== undefined
                    ? {
                          parent: {
                              connect: { id: Number(zonePayload.parent_id) },
                          },
                      }
                    : {}),
                ...(Array.isArray(zonePayload.selected_device_ids)
                    ? {
                          installed_devices: {
                              set: zonePayload.selected_device_ids
                                  .map((id) => Number(id))
                                  .filter(Number.isFinite)
                                  .map((id) => ({ id })),
                          },
                      }
                    : {}),
                updated_by: { connect: { id: Number(currentUserId) } },
            };
            await updateOneZone({
                variables: {
                    where: { id: Number(zonePayload.id) },
                    data,
                },
            });
        }
        await refetch();
    };

    const handleEditClick = (node) => {
        setEditingItem(node);
        setShowAddZoneDrawer(true);
    };
    const handleDeleteClick = (node) => {
        openConfirm(async () => {
            try {
                setDeletingNodeId(node.id);
                setConfirmLoading(true);
                await client.mutate({
                    mutation: DELETE_ONE_ZONE,
                    variables: { id: node.id },
                });
                await refetch();
            } catch (err) {
                starekContext.showError(err);
            } finally {
                setConfirmLoading(false);
                setDeletingNodeId(null);
                closeConfirm();
            }
        });
    };

    const handleAddNode = (parentId) => {
        const newId = items.length
            ? Math.max(...items.map((i) => i.id)) + 1
            : 1;
        const newNode = {
            id: newId,
            plant_id: starekContext.lastUsedPlant.id,
            parent_id: parentId,
            name: `New Zone ${newId}`,
            description: "Newly added zone",
            created_at: new Date().toISOString(),
        };
        setItems((prev) => {
            const updated = [...prev, newNode];
            if (!expanded.includes(parentId.toString())) {
                setExpanded((prevE) => [...prevE, parentId.toString()]);
            }
            return updated;
        });
    };

    const renderTree = (nodes, parentId = null, visited = new Set()) =>
        nodes
            .filter((n) => n.parent_id === parentId)
            .map((node) => {
                if (visited.has(node.id)) return null;
                visited.add(node.id);
                return (
                    <TreeItem
                        className="MuiTreeItem-root"
                        key={node.id}
                        nodeId={node.id.toString()}
                        label={
                            <div className="zone-label">
                                <span>{node.name}</span>
                                {/* <span className="zone-actions">
                  {node.parent_id !== null ? (
                    <>
                      <i
                        className={`fa fa-trash ${
                          deletingNodeId === node.id ? "text-muted" : ""
                        }`}
                        onClick={() => {
                          if (!deletingNodeId) handleDeleteClick(node);
                        }}
                        aria-label="Delete zone"
                        title="Delete"
                        role="button"
                      />
                      {deletingNodeId === node.id && (
                        <span
                          className="spinner-border spinner-border-sm ms-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                    </>
                  ) : (
                    <i className="fa fa-ban text-muted" />
                  )}
                  <i
                    className="fa fa-plus"
                    onClick={() => handleAddNode(node.id)}
                  />
                  <i
                    className="fa fa-pen"
                    onClick={() => handleEditClick(node)}
                  />
                </span> */}
                            </div>
                        }
                    >
                        {renderTree(nodes, node.id, visited)}
                    </TreeItem>
                );
            });

    const parentById = useMemo(() => {
        const m = new Map();
        items.forEach((z) => m.set(z.id, z));
        return m;
    }, [items]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (z) =>
                (z.name || "").toLowerCase().includes(q) ||
                (z.description || "").toLowerCase().includes(q)
        );
    }, [items, filterText]);

    const isSingle = selectedRows.length === 1;
    const isAny = selectedRows.length > 0;

    const columns = [
        {
            name: "Parent",
            selector: (r) =>
                r.parent_id ? parentById.get(r.parent_id)?.name || "-" : "-",
            width: "220px",
            height: "50px",
            sortable: true,
        },
        {
            name: "Name",
            height: "50px",
            selector: (r) => r.name || "-",
            sortable: true,
            grow: 1.2,
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            grow: 2,
            height: "50px",
            cell: (r) => (
                <div className="text-truncate h-50px" style={{ maxWidth: 250 }}>
                    {r.description || "-"}
                </div>
            ),
        },
        {
            name: "Created",
            height: "50px",
            selector: (r) =>
                r.created_at
                    ? new Date(r.created_at).toLocaleDateString()
                    : "-",
            width: "160px",
            sortable: true,
        },
    ];

    const handleAdd = () => {
        setEditingItem(null);
        setShowAddZoneDrawer(true);
    };
    const handleEditSelected = () => {
        if (!isSingle) return;
        setEditingItem(selectedRows[0]);
        setShowAddZoneDrawer(true);
    };
    const isAnySelected = selectedRows.length > 0;

    const handleBulkDelete = async () => {
        if (!isAnySelected || isDeleting) return;
        setIsDeleting(true);
        setDeleteDone(0);

        const ids = selectedRows.map((r) => r.id);

        try {
            await deleteManyZone({ variables: { ids } });
            setDeleteDone(ids.length);
        } catch {
            for (const id of ids) {
                try {
                    await deleteOneZone({ variables: { id } });
                    setDeleteDone((d) => d + 1);
                } catch (err) {
                    starekContext.showError?.(err);
                    break;
                }
            }
        } finally {
            setIsDeleting(false);
            setToggleCleared((p) => !p);
            await refetch();
        }
    };

    const onDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddZoneDrawer(false);
        setEditingItem(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        await refetch();
        if (itemDeleted) {
            navigator("/zones");
        }
    };

    if (loading || shadowLoading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-bullseye fa-10x text-success"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Zones</div>

            <div className="d-flex align-items-center justify-content-between mb-3">
                <div
                    className="d-flex align-items-center flex-grow-1"
                    style={{ gap: 12 }}
                >
                    {viewMode === "list" && (
                        <input
                            type="text"
                            className="border rounded p-2"
                            style={{ width: "40%" }}
                            placeholder="Search zones…"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    )}
                </div>

                <div className="d-flex align-items-center">
                    <div className="mr-4">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>List View</Tooltip>}
                        >
                            <Button
                                variant={
                                    viewMode === "list"
                                        ? "outline-primary"
                                        : "outline-secondary"
                                }
                                onClick={() => setViewMode("list")}
                                className="d-inline-flex align-items-center tw-h-10"
                            >
                                <i className="fa fa-list-ul" />
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Default View</Tooltip>}
                        >
                            <Button
                                variant={
                                    viewMode === "tree"
                                        ? "outline-primary"
                                        : "outline-secondary"
                                }
                                onClick={() => setViewMode("tree")}
                                className="d-inline-flex align-items-center ms-2 tw-h-10"
                            >
                                <i className="fa fa-sitemap" />
                            </Button>
                        </OverlayTrigger>
                    </div>
                    {viewMode === "list" && (
                        <>
                            <Button
                                className="me-2"
                                variant={
                                    isSingle ? "outline-primary" : "secondary"
                                }
                                disabled={!isSingle || !isAnySelected}
                                onClick={handleEditSelected}
                            >
                                Edit
                            </Button>
                            <Button
                                className="me-2"
                                variant="danger"
                                onClick={handleBulkDelete}
                                disabled={isDeleting || !isAnySelected}
                                aria-live="polite"
                            >
                                {isDeleting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Deleting {deleteDone}/
                                        {selectedRows.length}
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </Button>
                        </>
                    )}
                    <Button variant="primary" onClick={handleAdd}>
                        Add Zone
                    </Button>
                </div>
            </div>

            {viewMode === "tree" ? (
                <TreeView
                    expanded={expanded}
                    onNodeToggle={handleToggle}
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                >
                    {renderTree(items)}
                </TreeView>
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    selectableRows
                    onSelectedRowsChange={({ selectedRows }) =>
                        setSelectedRows(selectedRows)
                    }
                    clearSelectedRows={toggleCleared}
                    highlightOnHover
                    striped
                    pointerOnHover
                    responsive
                    pagination
                    dense
                    selectableRowsHighlight
                    selectableRowsNoSelectAll={false}
                    customStyles={customStyles}
                />
            )}

            {showAddZoneDrawer && (
                <AddZoneOffCanvasWizard
                    show={showAddZoneDrawer}
                    handleClose={(saved) => onDrawerClose(!!saved, false)}
                    item={editingItem || null}
                    plantId={starekContext.lastUsedPlant.id}
                    parentOptions={parentOptions}
                    deviceOptions={deviceOptions}
                    onSave={handleSaveZone}
                />
            )}

            {confirmationModalVisible && (
                <StarekCinfirmationModal
                    show={confirmationModalVisible}
                    handleClose={closeConfirm}
                    handleDeleteNode={onConfirmAction}
                    loading={confirmLoading}
                />
            )}
        </div>
    );
}

export { Zones };
